// src/Views/OrdersView.js
import React, { useState, useEffect, useContext } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';
import LogInOrSignUpView from './LogInOrSignUpView';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_COMPACT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
  SPACING_PAGE,
} from '../constants/layout';
import {
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_TITLE3,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

function OrdersView() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('userID', '==', user.uid)
      );

      const snapshot = await getDocs(ordersQuery);
      const ordersData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: data.orderID || doc.id,
          orderNumber: data.orderNumber,
          timestamp: data.timestamp?.toDate(),
          items: data.items || [],
          currency: data.currency || 'USD',
          statuses: (data.statuses || []).map(status => ({
            id: status.id,
            date: status.date?.toDate(),
            status: status.status,
          })),
          userName: data.userName,
          userEmail: data.userEmail,
          userPhone: data.userPhone,
        };
      });

      // Sort by timestamp, newest first
      ordersData.sort((a, b) => b.timestamp - a.timestamp);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      // Delay to match iOS app behavior
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const getStatusDisplay = (statusString) => {
    const statusMap = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      preparing: 'Preparing',
      readyForPickup: 'Ready for Pickup',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return statusMap[statusString] || statusString;
  };

  const getMostRecentStatus = (order) => {
    if (!order.statuses || order.statuses.length === 0) return null;
    return order.statuses.reduce((latest, current) =>
      current.date > latest.date ? current : latest
    );
  };

  const isCompleted = (order) => {
    return order.statuses?.some(status => status.status === 'completed') || false;
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => {
      const itemTotal = (item.unitAmount / 100.0) * item.quantity;
      return total + itemTotal;
    }, 0);
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  // Split orders into current and previous
  const currentOrders = orders.filter(order => !isCompleted(order));
  const previousOrders = orders.filter(order => isCompleted(order));

  if (authLoading) {
    return (
      <div className="orders-container loading">
        <div className="loading-spinner"></div>
        <style jsx>{`
          .orders-container.loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 400px;
          }

          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid var(--color-border);
            border-top-color: var(--color-accent);
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="orders-container">
          <div className="sign-in-prompt">
            <div className="prompt-icon">
              <span className="material-icons">receipt_long</span>
            </div>
            <h2 className="prompt-title">Sign In to View Orders</h2>
            <p className="prompt-description">
              Create an account or sign in to view your order history.
            </p>

            <button onClick={() => setShowAuth(true)} className="sign-in-button">
              Sign In
            </button>
          </div>

          <style jsx>{`
            .orders-container {
              max-width: ${MAX_CONTENT_WIDTH};
              margin: 0 auto;
              padding: 0 ${SPACING_CARD};
              min-height: calc(100vh - 200px);
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .sign-in-prompt {
              text-align: center;
              padding: ${SPACING_GROUP};
            }

            .prompt-icon {
              margin-bottom: ${SPACING_GROUP};
            }

            .prompt-icon .material-icons {
              font-size: 80px;
              color: var(--color-text-secondary);
              opacity: 0.5;
            }

            .prompt-title {
              font-size: ${FONT_TITLE3};
              font-weight: 600;
              margin: 0 0 ${SPACING_BUTTON} 0;
              color: var(--color-text-primary);
            }

            .prompt-description {
              font-size: ${FONT_BODY};
              color: var(--color-text-secondary);
              margin: 0 0 ${SPACING_SECTION} 0;
              max-width: 400px;
              margin-left: auto;
              margin-right: auto;
            }

            .sign-in-button {
              padding: ${SPACING_BUTTON} ${SPACING_SECTION};
              background-color: var(--color-accent);
              color: var(--color-accent-text);
              border: none;
              border-radius: ${BORDER_RADIUS};
              font-size: ${FONT_BODY};
              font-weight: 600;
              cursor: pointer;
              transition: background-color 0.2s ease;
            }

            .sign-in-button:hover {
              background-color: var(--color-accent-hover);
            }
          `}</style>
        </div>

        {showAuth && <LogInOrSignUpView onClose={() => setShowAuth(false)} />}
      </>
    );
  }

  // User is signed in
  return (
    <div className="orders-container">
      {orders.length > 0 && (
        <div className="orders-header">
          <h1 className="section-title">Orders</h1>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {!loading && orders.length === 0 && (
        <div className="empty-orders">
          <div className="empty-icon">
            <span className="material-icons">receipt_long</span>
          </div>
          <h2 className="empty-title">No Orders</h2>
          <p className="empty-description">
            Your order history is empty.
          </p>
        </div>
      )}

      {!loading && currentOrders.length > 0 && (
        <div className="orders-section">
          <h2 className="section-heading">Current Orders</h2>
          <div className="orders-list">
            {currentOrders.map(order => {
              const recentStatus = getMostRecentStatus(order);
              const total = calculateTotal(order.items);

              return (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div className="order-number">
                      Order #{String(order.orderNumber).padStart(3, '0')}
                    </div>
                    {recentStatus && (
                      <div className={`order-status status-${recentStatus.status}`}>
                        {getStatusDisplay(recentStatus.status)}
                      </div>
                    )}
                  </div>

                  <div className="order-details">
                    <div className="order-info">
                      <span className="info-label">Items:</span>
                      <span className="info-value">{order.items.length}</span>
                    </div>
                    <div className="order-info">
                      <span className="info-label">Total:</span>
                      <span className="info-value">{formatCurrency(total, order.currency)}</span>
                    </div>
                    <div className="order-info">
                      <span className="info-label">Date:</span>
                      <span className="info-value">{formatDate(order.timestamp)}</span>
                    </div>
                  </div>

                  {order.items.length > 0 && (
                    <div className="order-items">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="order-item">
                          <span className="item-quantity">{item.quantity}x</span>
                          <span className="item-name">{item.productName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && previousOrders.length > 0 && (
        <div className="orders-section">
          <h2 className="section-heading">Previous Orders</h2>
          <div className="orders-list">
            {previousOrders.map(order => {
              const recentStatus = getMostRecentStatus(order);
              const total = calculateTotal(order.items);

              return (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div className="order-number">
                      Order #{String(order.orderNumber).padStart(3, '0')}
                    </div>
                    {recentStatus && (
                      <div className={`order-status status-${recentStatus.status}`}>
                        {getStatusDisplay(recentStatus.status)}
                      </div>
                    )}
                  </div>

                  <div className="order-details">
                    <div className="order-info">
                      <span className="info-label">Items:</span>
                      <span className="info-value">{order.items.length}</span>
                    </div>
                    <div className="order-info">
                      <span className="info-label">Total:</span>
                      <span className="info-value">{formatCurrency(total, order.currency)}</span>
                    </div>
                    <div className="order-info">
                      <span className="info-label">Date:</span>
                      <span className="info-value">{formatDate(order.timestamp)}</span>
                    </div>
                  </div>

                  {order.items.length > 0 && (
                    <div className="order-items">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="order-item">
                          <span className="item-quantity">{item.quantity}x</span>
                          <span className="item-name">{item.productName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style jsx>{`
        .orders-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
          position: relative;
          min-height: calc(100vh - 200px);
        }

        .orders-container:has(.empty-orders) {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 ${SPACING_CARD};
        }

        .orders-header {
          margin-bottom: ${SPACING_SECTION};
        }

        .section-title {
          font-size: ${FONT_TITLE2};
          font-weight: 700;
          margin: 0;
          text-transform: uppercase;
          color: var(--color-text-primary);
        }

        .loading-overlay {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background-color: var(--color-surface);
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_CARD_BODY};
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-accent);
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .empty-orders {
          text-align: center;
          padding: ${SPACING_GROUP};
        }

        .empty-icon {
          margin-bottom: ${SPACING_GROUP};
        }

        .empty-icon .material-icons {
          font-size: 80px;
          color: var(--color-text-secondary);
          opacity: 0.5;
        }

        .empty-title {
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          margin: 0 0 ${SPACING_BUTTON} 0;
          color: var(--color-text-primary);
        }

        .empty-description {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          margin: 0;
        }

        .orders-section {
          margin-bottom: ${SPACING_HERO};
        }

        .orders-section:last-child {
          margin-bottom: 0;
        }

        .section-heading {
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          margin: 0 0 ${SPACING_CARD} 0;
          color: var(--color-text-primary);
        }

        .orders-list {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD};
        }

        .order-card {
          background-color: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_CARD_BODY};
        }

        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${SPACING_CARD};
          padding-bottom: ${SPACING_CARD};
          border-bottom: 1px solid var(--color-border);
        }

        .order-number {
          font-size: ${FONT_BODY};
          font-weight: 700;
          color: var(--color-text-primary);
        }

        .order-status {
          padding: ${SPACING_TIGHT} ${SPACING_BUTTON};
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
        }

        .status-pending {
          background-color: #fef3c7;
          color: #92400e;
        }

        .status-confirmed {
          background-color: #dbeafe;
          color: #1e40af;
        }

        .status-preparing {
          background-color: #fce7f3;
          color: #9f1239;
        }

        .status-readyForPickup {
          background-color: #dcfce7;
          color: #166534;
        }

        .status-completed {
          background-color: #d1fae5;
          color: #065f46;
        }

        .status-cancelled {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .order-details {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_COMPACT};
          margin-bottom: ${SPACING_CARD};
        }

        .order-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .info-label {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
        }

        .info-value {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-primary);
          font-weight: 600;
        }

        .order-items {
          padding-top: ${SPACING_CARD};
          border-top: 1px solid var(--color-border);
        }

        .order-item {
          display: flex;
          gap: ${SPACING_COMPACT};
          padding: ${SPACING_TIGHT} 0;
          font-size: ${FONT_FOOTNOTE};
        }

        .item-quantity {
          color: var(--color-text-secondary);
          font-weight: 600;
          min-width: 30px;
        }

        .item-name {
          color: var(--color-text-primary);
        }

        @media (max-width: 768px) {
          .section-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }

          .order-card {
            padding: ${SPACING_CARD};
          }
        }
      `}</style>
    </div>
  );
}

export default OrdersView;
